<script setup>
import { computed } from 'vue'

import { getCheckinNecessaryFieldsSet } from '@/utils/check-ins/check-ins'
import { AUTHOR_VARIANTS } from '@/utils/components-configurations/checkin-field'
import {
  SKELETON_APPEARANCES,
  SKELETON_SIZES
} from '@/utils/components-configurations/skeleton-item'
import { includesValue } from '@/utils/prop-validators'

import CheckinLayout from '@/components/objectives/element-check-in/CheckinLayout'
import CheckinField from '@/components/objectives/element-check-in/ChekinField'
import AppIcon from '@/components/ui/AppIcon/AppIcon'
import SkeletonItem from '@/components/ui/SkeletonLoaders/SkeletonItem'

defineOptions({
  name: 'CheckinPreviewLoader'
})

const props = defineProps({
  elementTypeId: {
    type: [String, Number],
    required: true
  },

  existContributeChildren: {
    type: Boolean
  },

  showElement: {
    type: Boolean
  },

  authorVariant: {
    type: String,
    default: AUTHOR_VARIANTS.DEFAULT,
    validator: v => includesValue(v, AUTHOR_VARIANTS)
  }
})

const isAuthorCompact = computed(() => props.authorVariant === AUTHOR_VARIANTS.COMPACT)

const loaderFields = computed(() => {
  return getCheckinNecessaryFieldsSet({
    typeId: props.elementTypeId,
    existContributeChildren: props.existContributeChildren
  })
})
</script>

<template>
  <CheckinLayout>
    <template #author>
      <div
        :class="{
          'cpl-Loader_Author-compact': isAuthorCompact
        }"
        class="cpl-Loader_Author"
      >
        <SkeletonItem
          :appearance="SKELETON_APPEARANCES.CIRCLE"
          :size="isAuthorCompact ? SKELETON_SIZES.LG : SKELETON_SIZES.SM"
        />

        <div v-if="isAuthorCompact" class="cpl-AuthorInfo">
          <SkeletonItem :size="SKELETON_SIZES.XS" width="160px" />
          <SkeletonItem :size="SKELETON_SIZES.XS" width="96px" />
        </div>

        <template v-else>
          <SkeletonItem :size="SKELETON_SIZES.XS" width="160px" />
          <SkeletonItem :size="SKELETON_SIZES.XS" width="120px" />
          <SkeletonItem :size="SKELETON_SIZES.XS" width="60px" />
        </template>
      </div>
    </template>

    <template #body>
      <div v-if="showElement" class="cpl-Element">
        <div class="cpl-Element_Name">
          <SkeletonItem :appearance="SKELETON_APPEARANCES.SQUARE" :size="SKELETON_SIZES.SM" />
          <SkeletonItem :size="SKELETON_SIZES.XS" width="60px" />
          <SkeletonItem :size="SKELETON_SIZES.XS" width="180px" />
        </div>

        <SkeletonItem :size="SKELETON_SIZES.XS" width="80px" />
        <SkeletonItem :size="SKELETON_SIZES.SM" width="220px" />
      </div>
      <CheckinField v-for="[key, field] in Object.entries(loaderFields)" :key="key" :field>
        <template #field-value>
          <div class="cpl-UpdatesLog_History">
            <SkeletonItem :size="SKELETON_SIZES.SM" width="64px" />

            <AppIcon class="cpl-Arrow" height="24" icon-name="arrow-right-next" width="24" />

            <SkeletonItem :size="SKELETON_SIZES.SM" width="72px" />
          </div>
        </template>
      </CheckinField>
    </template>
  </CheckinLayout>
</template>

<style lang="scss" scoped>
.cpl-Loader_Author {
  display: flex;
  align-items: center;
  gap: 6px;

  > :first-child {
    margin-right: 2px;
  }

  &-compact {
    > :first-child {
      margin-right: 4px;
    }
  }
}

.cpl-UpdatesLog_History {
  display: flex;
  align-items: center;
  gap: 4px;
  color: $dark-2;
}

.cpl-AuthorInfo {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-height: 44px;
  justify-content: center;
}

.cpl-Element {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-block: 7px 20px;
}

.cpl-Element_Name {
  display: flex;
  gap: 4px;
  align-items: center;
  margin-right: auto;

  > :first-child {
    margin-right: 4px;
  }
}
</style>
